import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { Calendar, Clock, Target, Zap, TrendingUp, Users, CheckCircle2, AlertCircle, ArrowRight, Sparkles, Play, PenLine, Lightbulb, FileText, Wand2 } from 'lucide-react';
import SprintDashboard from './SprintDashboard';
import EnhancedSprintBlock from '../Timeline/EnhancedSprintBlock';
import ShareButton from '../Sharing/ShareButton';

/**
 * Filter timeline items to only include actionable tasks (not reminders or events)
 * Excludes: deadline_reminders, reminders, events, and items without workflow_key
 */
const getActionableTasks = (timelineItems) => {
  if (!timelineItems || !Array.isArray(timelineItems)) return [];

  return timelineItems.filter(item => {
    // Exclude deadline reminders - these are notifications, not work tasks
    if (item.source_type === 'deadline_reminder') return false;

    // Exclude other non-actionable types
    if (['reminder', 'event', 'college_deadline', 'deadline'].includes(item.source_type)) {
      return false;
    }

    // Only workflow-structured tasks are actionable
    if (!item.workflow_key) return false;

    // Exclude completed and skipped tasks (check all possible completion/skip fields)
    if (item.completion_status === 'completed' ||
        item.completion_status === 'skipped' ||
        item.completed ||
        item.status === 'completed' ||
        item.status === 'skipped') return false;

    return true;
  });
};

export default function CollegeJourneyHubV2() {
  const { currentUser } = useFamilyContext();
  const {
    colleges,
    timeline,
    essays,
    deadlines,
    loading,
    loadCollegeJourneyData,
    loadCollegeDeadlines
  } = useCollegeContext();

  // Debug: Log context data on every render (commented out to reduce console noise)
  // console.log('[CollegeJourneyHubV2] Context Data:', {
  //   loading,
  //   collegesCount: colleges?.length,
  //   hasDeadlines: !!deadlines,
  //   deadlinesIsMap: deadlines instanceof Map,
  //   timelineCount: timeline?.length,
  //   currentUserId: currentUser?.id
  // });

  const [essayFlowType, setEssayFlowType] = useState('supplemental'); // 'supplemental' or 'personal'
  const [currentEssayIndex, setCurrentEssayIndex] = useState(0);
  const [essaySlots, setEssaySlots] = useState([]);
  const [essayIdeas, setEssayIdeas] = useState([]);
  const [essayOutlines, setEssayOutlines] = useState([]);
  const [essayDrafts, setEssayDrafts] = useState([]);

  // Load essay slots and related data from database
  useEffect(() => {
    const loadEssayData = async () => {
      if (!currentUser?.id) return;

      try {
        // Load essay slots
        const { data: slotsData, error: slotsError } = await supabase
          .from('essay_slots')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false });

        if (slotsError) throw slotsError;
        setEssaySlots(slotsData || []);

        // Load essay ideas (topic development in progress or complete)
        const { data: ideasData, error: ideasError } = await supabase
          .from('college_journey_essay_ideas')
          .select('*')
          .eq('user_id', currentUser.id);

        if (ideasError) throw ideasError;
        setEssayIdeas(ideasData || []);

        // Load essay outlines (topic development complete, ready for builder)
        const { data: outlinesData, error: outlinesError } = await supabase
          .from('college_journey_essay_outlines')
          .select('*')
          .eq('user_id', currentUser.id);

        if (outlinesError) throw outlinesError;
        setEssayOutlines(outlinesData || []);

        // Load essay drafts (builder complete)
        const { data: draftsData, error: draftsError } = await supabase
          .from('college_journey_essays')
          .select('*')
          .eq('user_id', currentUser.id);

        if (draftsError) throw draftsError;
        setEssayDrafts(draftsData || []);
      } catch (error) {
        console.error('[CollegeJourneyHubV2] Error loading essay data:', error);
      }
    };

    loadEssayData();
  }, [currentUser?.id]);

  // Get prioritized essays (ED > EA > RD by deadline)
  const prioritizedEssays = useMemo(() => {
    // Use essay_slots instead of essays from context
    if (!essaySlots?.length || !colleges) {
      return [];
    }

    // Process essay slots - they all have college_id
    const supplementalEssays = essaySlots.filter(e => {
      const hasCollege = e.college_id !== null && e.college_id !== undefined;
      // Only show essays that have a college association
      return hasCollege;
    });

    // For essay_slots, we need to match them to colleges
    // They may have application_round directly, or we get it from timeline
    const essaysWithCollegeInfo = supplementalEssays.map(essay => {
      // Try to find college by:
      // 1. Direct college_id on the slot
      // 2. Timeline item's college_id
      // 3. Application round matching
      let college = null;
      let collegeId = essay.college_id;

      if (!collegeId && essay.timeline_item_id && timeline) {
        const timelineItem = timeline.find(t => t.id === essay.timeline_item_id);
        collegeId = timelineItem?.college_id;
      }

      if (collegeId) {
        college = colleges.find(c => c.id === collegeId);
      } else if (essay.application_round) {
        // If slot has application_round, match to first college with that round
        college = colleges.find(c => c.application_round === essay.application_round);
      }

      const applicationRound = essay.application_round || college?.application_round;

      return {
        ...essay,
        college,
        application_round: applicationRound,
        deadline: deadlines?.get?.(college?.id)?.find?.(d => d.deadline_type === 'application')?.date,
        prompt: essay.prompt_text, // Map prompt_text to prompt for display
        word_count: essay.word_limit // Map word_limit to word_count for display
      };
    }).filter(e => {
      // For now, include all essay slots even if we don't have college match
      // This way user can still see their essays
      return true; // Include all for now
    });

    // Sort by: ED first, then EA, then RD, and within each group by deadline
    essaysWithCollegeInfo.sort((a, b) => {
      // Priority order
      const priorityOrder = { 'early_decision': 1, 'early_action': 2, 'regular_decision': 3 };
      const aPriority = priorityOrder[a.application_round] || 999;
      const bPriority = priorityOrder[b.application_round] || 999;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // Within same priority, sort by deadline
      if (a.deadline && b.deadline) {
        return new Date(a.deadline) - new Date(b.deadline);
      }
      if (a.deadline) return -1;
      if (b.deadline) return 1;
      return 0;
    });

    return essaysWithCollegeInfo.slice(0, 5); // Limit to 5 essays
  }, [essaySlots, colleges, deadlines]);

  // Current essay for display
  const currentEssay = prioritizedEssays[currentEssayIndex];

  // Check essay development status by looking at which related tables have entries
  // Memoized to prevent excessive re-renders
  const essayStatus = useMemo(() => {
    if (!currentEssay) return null;

    // Check which related tables have entries for this essay_slot
    const hasIdea = essayIdeas.some(idea => idea.essay_slot_id === currentEssay.id);
    const hasOutline = essayOutlines.some(outline => outline.essay_slot_id === currentEssay.id);
    const hasDraft = essayDrafts.some(draft => draft.essay_slot_id === currentEssay.id);

    // Determine next step and statuses based on table existence
    let nextStep = null;
    let topicDevStatus = null;
    let topicDevClickable = false;
    let builderStatus = null;
    let builderClickable = false;
    let outlineComplete = false;
    let draftComplete = false;

    // Case 4: essay_slots + ideas + outlines + drafts = Draft complete, ready for analysis
    if (hasIdea && hasOutline && hasDraft) {
      outlineComplete = true;
      draftComplete = true;
      topicDevStatus = 'Outline Ready';
      builderStatus = 'First Draft Ready';
      nextStep = 'analysis'; // Step 4 would be next
    }
    // Case 3: essay_slots + ideas + outlines = Outline complete, ready for builder
    else if (hasIdea && hasOutline) {
      outlineComplete = true;
      topicDevStatus = 'Outline Ready';
      builderStatus = 'Ready to Draft';
      builderClickable = true;
      nextStep = 'builder';
    }
    // Case 2: essay_slots + ideas = Topic dev in progress
    else if (hasIdea) {
      topicDevStatus = 'In Progress';
      topicDevClickable = true;
      nextStep = 'topic_dev';
    }
    // Case 1: essay_slots only = Ready to start topic development
    else {
      topicDevStatus = 'Ready to Outline';
      topicDevClickable = true;
      nextStep = 'topic_dev';
    }

    return {
      hasIdea,
      hasOutline: outlineComplete,
      hasDraft,
      draftComplete,
      topicDevStatus,
      builderStatus,
      nextStep,
      topicDevClickable,
      builderClickable
    };
  }, [currentEssay, essayIdeas, essayOutlines, essayDrafts]);

  // Essay ideation tools to rotate through
  const essayIdeationTools = useMemo(() => [
    {
      id: 'moments',
      title: 'Find Essay Ideas in Your Memories',
      subtitle: 'Moments That Matter',
      description: 'Discover topics in the details you notice and moments you remember',
      timeEstimate: '15 min',
      icon: '💭',
      color: 'gold',
      route: '/college/essays/ideas?tool=browse'
    },
    {
      id: 'competencies',
      title: 'Uncover Your Hidden Strengths',
      subtitle: 'AI-Guided Interview',
      description: 'AI-guided conversation reveals your unique competencies and unexpected stories',
      timeEstimate: '20 min',
      icon: '🎯',
      color: 'teal',
      route: '/college/essays/ideas?tool=competencies'
    },
    {
      id: 'identity',
      title: 'Explore What Makes You Unique',
      subtitle: 'Character & Identity',
      description: 'Deep-dive into your values, experiences, and authentic self',
      timeEstimate: '30 min',
      icon: '✨',
      color: 'indigo',
      route: '/college/essays/ideas?tool=identity'
    },
    {
      id: 'objects',
      title: 'Tell Your Story Through Objects',
      subtitle: 'Sentimental Objects',
      description: 'Find essay topics in the things that matter to you',
      timeEstimate: '15 min',
      icon: '📦',
      color: 'azure',
      route: '/college/essays/ideas?tool=essay_generator'
    }
  ], []);

  // Rotate through tools every 10 seconds
  const [currentToolIndex, setCurrentToolIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentToolIndex((prev) => (prev + 1) % essayIdeationTools.length);
    }, 10000); // Rotate every 10 seconds

    return () => clearInterval(interval);
  }, [essayIdeationTools.length]);

  // Note: Data loading is handled by CollegeProvider automatically
  // No need to call loadCollegeJourneyData here - it would be redundant

  // Load deadlines for all colleges when colleges are available
  useEffect(() => {
    if (colleges && colleges.length > 0) {
      colleges.forEach(college => {
        loadCollegeDeadlines(college.id);
      });
    }
  }, [colleges?.length]); // Only re-run when college count changes

  // State for showing all schools with same deadline
  const [showAllDeadlineSchools, setShowAllDeadlineSchools] = useState(false);

  // Calculate soonest deadline and priority deadline (ED if exists)
  const { nearestDeadline, deadlineCollegeName: deadlineCollege, deadlineLabel: deadlineType, priorityDeadline, priorityCollegeName, priorityDeadlineLabel, deadlineColleges } = useMemo(() => {
    console.log('🔍 [COUNTDOWN DEBUG] Starting deadline calculation...');
    console.log('📊 [COUNTDOWN DEBUG] Colleges count:', colleges?.length);
    console.log('📊 [COUNTDOWN DEBUG] Deadlines map size:', deadlines?.size);

    if (!deadlines || !colleges || colleges.length === 0) {
      console.log('⚠️ [COUNTDOWN DEBUG] No deadlines or colleges, returning empty state');
      return {
        nearestDeadline: null,
        deadlineCollegeName: null,
        deadlineLabel: 'Deadline',
        priorityDeadline: null,
        priorityCollegeName: null,
        priorityDeadlineLabel: null,
        deadlineColleges: []
      };
    }

    // Helper function to check if a deadline has passed (considering 11:59pm as cutoff)
    const hasDeadlinePassed = (deadlineDate) => {
      const now = new Date();
      // Set deadline to end of day (11:59:59 PM)
      const deadlineEndOfDay = new Date(deadlineDate);
      deadlineEndOfDay.setHours(23, 59, 59, 999);

      const hasPassed = now > deadlineEndOfDay;
      console.log(`⏰ [COUNTDOWN DEBUG] Deadline ${deadlineDate.toLocaleDateString()} ${hasPassed ? 'HAS PASSED' : 'is upcoming'} (cutoff: ${deadlineEndOfDay.toLocaleString()})`);
      return hasPassed;
    };

    // Helper to match deadline category to application round
    const deadlineCategoryMatches = (college, deadline) => {
      const round = college.application_round;
      const category = deadline.category;

      // Map application rounds to their corresponding deadline categories
      let matches = false;
      if (round === 'early_decision' && (category === 'early_decision' || category === 'early_decision_2')) {
        matches = true;
      }
      if (round === 'early_action' && category === 'early_action') {
        matches = true;
      }
      if (round === 'regular_decision' && category === 'regular_decision') {
        matches = true;
      }

      console.log(`🎯 [COUNTDOWN DEBUG] ${college.name}: round="${round}" vs category="${category}" → ${matches ? '✅ MATCH' : '❌ NO MATCH'}`);
      return matches;
    };

    // Step 1: Collect all application deadlines that match student's selected rounds
    // and exclude those that have passed
    const upcomingDeadlines = [];

    console.log('📋 [COUNTDOWN DEBUG] Processing colleges...');
    colleges.forEach(college => {
      console.log(`\n🏫 [COUNTDOWN DEBUG] College: ${college.name}`);
      console.log(`   Application round: ${college.application_round}`);

      const collegeDeadlines = deadlines.get?.(college.id) || [];
      console.log(`   Found ${collegeDeadlines.length} deadlines for this college`);

      collegeDeadlines.forEach(deadline => {
        console.log(`   📅 Deadline: type="${deadline.deadline_type}", category="${deadline.category}", date="${deadline.date}"`);

        // Only process application deadlines
        if (deadline.deadline_type !== 'application' || !deadline.date) {
          console.log(`      ⏭️ Skipping: not an application deadline or no date`);
          return;
        }

        // Only include deadlines that match the student's selected application round
        if (!deadlineCategoryMatches(college, deadline)) {
          console.log(`      ⏭️ Skipping: category doesn't match application round`);
          return;
        }

        const deadlineDate = new Date(deadline.date);

        // Exclude deadlines that have passed (using 11:59pm cutoff)
        if (hasDeadlinePassed(deadlineDate)) {
          console.log(`      ⏭️ Skipping: deadline has passed`);
          return;
        }

        console.log(`      ✅ Adding to upcoming deadlines`);
        upcomingDeadlines.push({
          college,
          deadline: deadlineDate,
          round: college.application_round,
          category: deadline.category
        });
      });
    });

    console.log(`\n📊 [COUNTDOWN DEBUG] Total upcoming deadlines collected: ${upcomingDeadlines.length}`);
    upcomingDeadlines.forEach((item, idx) => {
      console.log(`   ${idx + 1}. ${item.college.name} - ${item.round} - ${item.deadline.toLocaleDateString()}`);
    });

    // If no upcoming deadlines, return empty state
    if (upcomingDeadlines.length === 0) {
      console.log('⚠️ [COUNTDOWN DEBUG] No upcoming deadlines found, returning empty state');
      return {
        nearestDeadline: null,
        deadlineCollegeName: null,
        deadlineLabel: 'Deadline',
        priorityDeadline: null,
        priorityCollegeName: null,
        priorityDeadlineLabel: null,
        deadlineColleges: []
      };
    }

    // Step 2: Sort by priority (ED > EA > RD) then by date
    console.log('\n🔀 [COUNTDOWN DEBUG] Sorting deadlines by priority...');
    const priorityOrder = {
      'early_decision': 1,
      'early_action': 2,
      'regular_decision': 3
    };

    upcomingDeadlines.sort((a, b) => {
      const aPriority = priorityOrder[a.round] || 999;
      const bPriority = priorityOrder[b.round] || 999;

      // First sort by priority
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // Then by date (earliest first)
      return a.deadline - b.deadline;
    });

    console.log('📊 [COUNTDOWN DEBUG] Sorted deadlines:');
    upcomingDeadlines.forEach((item, idx) => {
      console.log(`   ${idx + 1}. ${item.college.name} - ${item.round} (priority ${priorityOrder[item.round]}) - ${item.deadline.toLocaleDateString()}`);
    });

    // Step 3: Get the soonest deadline (first in sorted list)
    const soonest = upcomingDeadlines[0];
    const soonestDeadline = soonest.deadline;
    const soonestCollege = soonest.college;
    const soonestRound = soonest.round;

    console.log(`\n🎯 [COUNTDOWN DEBUG] Soonest deadline selected: ${soonestCollege.name} - ${soonestRound} - ${soonestDeadline.toLocaleDateString()}`);

    // Step 4: Find all colleges with the same deadline date AND same round
    const collegesWithSoonestDeadline = upcomingDeadlines.filter(item => {
      const isSameDate = item.deadline.toDateString() === soonestDeadline.toDateString();
      const isSameRound = item.round === soonestRound;
      return isSameDate && isSameRound;
    });

    console.log(`📋 [COUNTDOWN DEBUG] Colleges sharing soonest deadline: ${collegesWithSoonestDeadline.length}`);
    collegesWithSoonestDeadline.forEach((item, idx) => {
      console.log(`   ${idx + 1}. ${item.college.name}`);
    });

    // Deduplicate by college ID
    const uniqueColleges = [];
    const seenCollegeIds = new Set();

    collegesWithSoonestDeadline.forEach(({ college, round }) => {
      if (!seenCollegeIds.has(college.id)) {
        seenCollegeIds.add(college.id);
        uniqueColleges.push({ college, round });
      }
    });

    // Step 5: Find priority ED deadline (if exists and different from soonest)
    let edDeadline = null;
    let edCollege = null;

    const edDeadlines = upcomingDeadlines.filter(item => item.round === 'early_decision');
    if (edDeadlines.length > 0) {
      edDeadline = edDeadlines[0].deadline;
      edCollege = edDeadlines[0].college;
      console.log(`🎓 [COUNTDOWN DEBUG] ED deadline found: ${edCollege.name} - ${edDeadline.toLocaleDateString()}`);
    } else {
      console.log(`🎓 [COUNTDOWN DEBUG] No ED deadlines found`);
    }

    // Format the labels based on application round
    const formatLabel = (round) => {
      if (round === 'early_decision') return 'Early Decision';
      if (round === 'early_action') return 'Early Action';
      if (round === 'regular_decision') return 'Regular Decision';
      return 'Deadline';
    };

    const soonestLabel = formatLabel(soonestRound);

    console.log(`\n✅ [COUNTDOWN DEBUG] Final result: "${soonestCollege.name} ${soonestLabel}" on ${soonestDeadline.toLocaleDateString()}`);
    console.log(`📊 [COUNTDOWN DEBUG] Showing ${uniqueColleges.length} colleges with this deadline\n`);

    return {
      nearestDeadline: soonestDeadline,
      deadlineCollegeName: soonestCollege?.name || null,
      deadlineLabel: soonestLabel,
      priorityDeadline: edDeadline,
      priorityCollegeName: edCollege?.name || null,
      priorityDeadlineLabel: 'Early Decision',
      deadlineColleges: uniqueColleges
    };
  }, [colleges, deadlines]);

  // Countdown timer for nearest deadline
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    if (!nearestDeadline) return;

    const updateCountdown = () => {
      const now = new Date();
      const diff = nearestDeadline - now;

      if (diff <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [nearestDeadline]);

  // Get upcoming tasks for "Managing Your Week"
  const upcomingTasks = useMemo(() => {
    // Get only actionable tasks (excludes deadline_reminders, events, etc.)
    const actionableTasks = getActionableTasks(timeline);

    return actionableTasks
      .sort((a, b) => {
        // Sort by due date, then priority
        if (a.due_date && b.due_date) {
          return new Date(a.due_date) - new Date(b.due_date);
        }
        if (a.due_date) return -1;
        if (b.due_date) return 1;
        return 0;
      })
      .slice(0, 3);
  }, [timeline]);

  // Get ED/EA colleges
  const earlyColleges = useMemo(() => {
    if (!colleges || !Array.isArray(colleges)) return [];

    return colleges.filter(c =>
      c.application_round === 'early_decision' ||
      c.application_round === 'early_action'
    );
  }, [colleges]);

  // Get completion status for each college
  function getCollegeStatus(college) {
    // Get only actionable tasks (excludes deadline_reminders, events, etc.)
    const actionableTasks = getActionableTasks(timeline);

    // Count incomplete tasks for this college
    const incompleteTasks = actionableTasks.filter(item =>
      item.college_id === college.id
    ).length;

    if (incompleteTasks === 0) return { status: 'ready', label: 'Ready to submit' };
    if (incompleteTasks <= 2) return { status: 'almost', label: `${incompleteTasks} items left` };
    return { status: 'needs_work', label: 'Needs work today' };
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-stone-600">Loading your college journey...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">

      {/* Header with Countdown */}
      <div className="bg-slate-900 text-white py-4 sm:py-6 px-4 sm:px-6 lg:px-8 border-b-2 border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-auto text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold mb-1">College Journey</h1>
              <div className="text-xs sm:text-sm text-stone-400">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>

            {/* College Deadlines - Always visible */}
              {nearestDeadline ? (
                <>
                  {/* Countdown Timer - Featured Countdown */}
                  <div className="w-full sm:w-auto">
                  <div className="text-base sm:text-lg font-bold mb-1 flex items-center justify-center sm:justify-end gap-2 flex-wrap">
                    <span className="text-center">{deadlineCollege} {deadlineType}</span>

                  </div>
                  <div className="flex items-center justify-center gap-2 sm:gap-3 text-xl sm:text-2xl font-bold tabular-nums mb-3">
                    <div className="flex flex-col items-center">
                      <span>{timeRemaining.days}</span>
                      <span className="text-[10px] sm:text-xs text-stone-400 font-normal">days</span>
                    </div>
                    <span className="text-stone-600">:</span>
                    <div className="flex flex-col items-center">
                      <span>{String(timeRemaining.hours).padStart(2, '0')}</span>
                      <span className="text-[10px] sm:text-xs text-stone-400 font-normal">hrs</span>
                    </div>
                    <span className="text-stone-600">:</span>
                    <div className="flex flex-col items-center">
                      <span>{String(timeRemaining.minutes).padStart(2, '0')}</span>
                      <span className="text-[10px] sm:text-xs text-stone-400 font-normal">min</span>
                    </div>
                    <span className="text-stone-600">:</span>
                    <div className="flex flex-col items-center">
                      <span>{String(timeRemaining.seconds).padStart(2, '0')}</span>
                      <span className="text-[10px] sm:text-xs text-stone-400 font-normal">sec</span>
                    </div>
                  </div>
              </div>
                          

                  {/* Dropdown showing all schools with same deadline */}
                  <div className="flex flex-col items-center sm:items-end w-full sm:w-auto">
                  {showAllDeadlineSchools && deadlineColleges && deadlineColleges.length > 1 && (
                    <div className="mb-3 text-sm flex flex-col items-center sm:items-end w-full">
                      <button
                        onClick={() => setShowAllDeadlineSchools(false)}
                        className="text-stone-50 bg-teal-500 text-xs p-2 rounded-lg hover:text-white flex items-center justify-center gap-1 mb-2 w-full sm:w-auto"
                      >
                        Show all {deadlineColleges.length} schools due on {nearestDeadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}

                      </button>
                      <div className="rounded-lg p-3 space-y-1.5 w-full">
                        {deadlineColleges.map(({ college, round }, idx) => (
                          <div key={idx} className="text-stone-300 flex items-center justify-center sm:justify-end gap-2 text-xs">
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-400"></div>
                            <span className="truncate">{college.name} {round === 'early_decision' ? 'ED' : round === 'early_action' ? 'EA' : 'RD'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Collapsed view - just show button */}
                  {!showAllDeadlineSchools && deadlineColleges && deadlineColleges.length > 1 && (
                    <button
                      onClick={() => setShowAllDeadlineSchools(true)}
                      className="bg-teal-500 text-xs text-stone-50 p-2 rounded-lg hover:text-white flex items-center justify-center gap-1 mb-3 w-full sm:w-auto"
                    >
                      <span className="truncate">Show all {deadlineColleges.length} schools due on {nearestDeadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>

                    </button>
                  )}

                  {/* Show priority deadline if it's different from soonest */}
                  {priorityDeadline && priorityDeadline > new Date() && priorityCollegeName !== deadlineCollege ? (
                    <div className="flex items-center justify-center sm:justify-end gap-2 text-xs pt-1 border-t border-stone-500 pt-2 flex-wrap">
                      <Target className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                      <span className="text-white truncate">
                        {priorityCollegeName} {priorityDeadlineLabel}
                      </span>
                      <span className="text-stone-400 whitespace-nowrap">
                        in {Math.ceil((priorityDeadline - new Date()) / (1000 * 60 * 60 * 24))} days
                      </span>
                    </div>
                  ) : priorityDeadline && priorityDeadline > new Date() && (
                    <div className="flex items-center justify-center sm:justify-end gap-2 text-xs sm:text-sm border-t border-stone-700 pt-3 flex-wrap">
                      <Target className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                      <span className="text-stone-300">
                        Next major deadline:
                      </span>
                      <span className="text-stone-400 truncate">
                        {priorityDeadlineLabel} ({priorityCollegeName}) in {Math.ceil((priorityDeadline - new Date()) / (1000 * 60 * 60 * 24))} days
                      </span>
                    </div>
                  )}
                  </div>                 
                </>
              ) : (
                <div className="text-sm text-stone-500 italic">
                  Add colleges to see countdown
                </div>
              )}
            </div>
            </div>
          </div>

      {/* Sprint Dashboard - Preset Sprint System */}
      <div className="max-w-7xl mx-auto my-2 mb-4 sm:mb-8 px-4 sm:px-6 lg:px-8">
        <SprintDashboard userId={currentUser?.id} colleges={colleges} />
      </div>

      {/* Fallback: Custom Sprint Block (if no preset sprints) */}
      {/* <div className="max-w-7xl mx-auto my-2 mb-8 px-4 sm:px-6 lg:px-8">
        <EnhancedSprintBlock timelineItems={timeline} colleges={colleges} />
      </div> */}

      {/* Two Column Layout */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 px-4 sm:px-6 lg:px-8">

        {/* Left Column - This Week */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">

          {/* Active Crisis Management */}
          <div className="bg-white border-2 border-slate-900 rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-coral-500"></div>
              <h3 className="font-bold text-slate-900 text-sm sm:text-base">Managing Your Week</h3>
            </div>

            <div className="space-y-3">
              {upcomingTasks.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-stone-600">No upcoming tasks</p>
                </div>
              ) : (
                upcomingTasks.map(task => (
                  <div key={task.id} className="flex items-start gap-2 sm:gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 break-words">{task.title || task.description}</div>
                      {task.due_date && (
                        <div className="text-xs text-coral-600 font-medium">
                          Due {new Date(task.due_date).toLocaleDateString()}
                        </div>
                      )}
                      {task.notes && (
                        <p className="text-xs text-stone-600 mt-1 break-words">{task.notes}</p>
                      )}
                    </div>
                    <button className="text-xs font-medium text-teal-600 border border-teal-600 px-2 sm:px-3 py-1.5 rounded hover:bg-teal-50 whitespace-nowrap flex-shrink-0">
                      Start
                    </button>
                  </div>
                ))
              )}
            </div>

            <button className="w-full mt-4 text-xs sm:text-sm text-stone-600 hover:text-slate-900 font-medium">
              Show all tasks →
            </button>
          </div>

          {/* Application Progress */}
          <div className="bg-white border border-stone-300 rounded-lg p-4 sm:p-6">
            <h3 className="font-bold text-slate-900 mb-2 text-sm sm:text-base">Early Decision & Early Action</h3>
            {nearestDeadline && earlyColleges.length > 0 && (
              <div className="text-xs sm:text-sm text-coral-600 font-medium mb-4 break-words">
                {earlyColleges.filter(c => c.application_round === 'early_decision' || c.application_round === 'early_action').length > 1 ? 'All due' : 'Due'} {nearestDeadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} ({Math.ceil((nearestDeadline - new Date()) / (1000 * 60 * 60 * 24))} days away)
              </div>
            )}

            {earlyColleges.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-stone-600">No early applications yet</p>
                <p className="text-xs text-stone-500 mt-1">Add colleges to get started</p>
              </div>
            ) : (
              <>
                {/* Today's Focus */}
                {(() => {
                  const needsWork = earlyColleges.filter(c => getCollegeStatus(c).status === 'needs_work');
                  const almostDone = earlyColleges.filter(c => getCollegeStatus(c).status === 'almost');
                  const ready = earlyColleges.filter(c => getCollegeStatus(c).status === 'ready');

                  return (
                    <>
                      {needsWork.length > 0 && (
                        <div className="mb-4">
                          <div className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2 flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                            Today's Focus
                          </div>
                          <div className="space-y-2">
                            {needsWork.slice(0, 2).map(college => {
                              const status = getCollegeStatus(college);
                              // Get actionable tasks for this college
                              const actionableTasks = getActionableTasks(timeline);
                              const collegeTasks = actionableTasks.filter(t => t.college_id === college.id);
                              const nextTask = collegeTasks[0];

                              return (
                                <div key={college.id} className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-slate-900 text-sm truncate">{college.name}</div>
                                      <div className="text-xs text-stone-600 mt-0.5">
                                        {college.application_round === 'early_decision' ? 'Early Decision' : 'Early Action'}
                                        {nextTask && ` • ${nextTask.title || nextTask.description}`}
                                      </div>
                                    </div>
                                    <button className="text-xs font-medium text-indigo-600 border border-indigo-600 px-3 py-1.5 rounded hover:bg-indigo-100 whitespace-nowrap flex-shrink-0">
                                      Continue →
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {almostDone.length > 0 && (
                        <div className="mb-4">
                          <div className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">Almost Done</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {almostDone.map(college => {
                              const status = getCollegeStatus(college);
                              return (
                                <div key={college.id} className="border border-stone-200 rounded-lg p-3">
                                  <div className={`w-2 h-2 rounded-full mb-2 bg-gold-500`}></div>
                                  <div className="font-medium text-slate-900 text-sm truncate">{college.name}</div>
                                  <div className="text-xs text-stone-600 mt-0.5">
                                    {college.application_round === 'early_decision' ? 'Early Decision' : 'Early Action'}
                                  </div>
                                  <div className="text-xs mt-2 font-medium text-stone-700">
                                    {status.label}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {ready.length > 0 && (
                        <div>
                          <div className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">Ready to Submit</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {ready.map(college => (
                              <div key={college.id} className="border border-teal-200 bg-teal-50 rounded-lg p-3">
                                <div className="w-2 h-2 rounded-full mb-2 bg-teal-500"></div>
                                <div className="font-medium text-slate-900 text-sm truncate">{college.name}</div>
                                <div className="text-xs text-stone-600 mt-0.5">
                                  {college.application_round === 'early_decision' ? 'ED' : 'EA'}
                                </div>
                                <div className="text-xs mt-2 font-medium text-teal-600">
                                  Ready to submit ✓
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {needsWork.length === 0 && almostDone.length === 0 && ready.length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-sm text-stone-600">No early applications in progress</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>

        {/* Right Column - Context & Strategy */}
        <div className="space-y-4 sm:space-y-6">

          {/* Timeline Health */}
          <div className="bg-slate-900 text-white rounded-lg p-4 sm:p-6">
            <div className="text-xs font-medium uppercase tracking-wide mb-3 text-stone-400">Timeline Status</div>
            <div className="text-3xl font-bold mb-2">On track</div>
            <p className="text-sm text-stone-300 mb-4">You're making solid progress. Here's where you stand:</p>

            {/* Milestone-Based Progress */}
            {earlyColleges.length > 0 && (
              <div className="mb-4 space-y-3">
                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-stone-300">
                      Early Decision applications ({earlyColleges.filter(c => c.application_round === 'early_decision').length > 0 ?
                        nearestDeadline?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No ED'})
                    </span>
                    <span className="text-teal-400 font-medium">On schedule ✓</span>
                  </div>
                  <div className="text-xs text-stone-400">
                    {(() => {
                      const totalEarlyColleges = earlyColleges.length;
                      const readyColleges = earlyColleges.filter(c => getCollegeStatus(c).status === 'ready').length;
                      const almostColleges = earlyColleges.filter(c => getCollegeStatus(c).status === 'almost').length;

                      if (readyColleges > 0) {
                        return `${readyColleges} ${readyColleges === 1 ? 'application' : 'applications'} ready to submit`;
                      } else if (almostColleges > 0) {
                        return `${almostColleges} ${almostColleges === 1 ? 'application' : 'applications'} nearly complete`;
                      } else {
                        return `${totalEarlyColleges} ${totalEarlyColleges === 1 ? 'application' : 'applications'} in progress`;
                      }
                    })()}
                  </div>
                </div>

                {colleges.some(c => c.application_round === 'regular_decision') && (
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-stone-300">Regular Decision prep (Jan 1)</span>
                      <span className="text-teal-400 font-medium">Ahead of plan ✓</span>
                    </div>
                    <div className="text-xs text-stone-400">
                      Early start gives you flexibility later
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Summary Metrics */}
            <div className="space-y-2 text-xs border-t border-stone-700 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-stone-400">Tasks completed this week</span>
                <span className="font-medium">
                  {timeline?.filter(t => {
                    if (t.status !== 'completed') return false;
                    const completedDate = t.completed_at ? new Date(t.completed_at) : null;
                    if (!completedDate) return false;
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return completedDate > weekAgo;
                  }).length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-400">Applications ready for submission</span>
                <span className="font-medium">
                  {colleges?.filter(c => getCollegeStatus(c).status === 'ready').length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-400">Focus this week</span>
                <span className="font-medium text-stone-200">
                  {earlyColleges.length > 0 ? 'Polish ED essays' : 'Build your college list'}
                </span>
              </div>
            </div>
          </div>

          {/* Essay Ideation Tool Recommendation - Rotating */}
          <div className="bg-white border border-stone-300 rounded-lg p-4 sm:p-6 transition-all duration-500">
            {(() => {
              const currentTool = essayIdeationTools[currentToolIndex];
              const colorClasses = {
                gold: { bg: 'bg-gold-500', text: 'text-gold-700', border: 'border-gold-500', light: 'bg-gold-50' },
                teal: { bg: 'bg-teal-500', text: 'text-teal-700', border: 'border-teal-500', light: 'bg-teal-50' },
                indigo: { bg: 'bg-indigo-500', text: 'text-indigo-700', border: 'border-indigo-500', light: 'bg-indigo-50' },
                azure: { bg: 'bg-azure-500', text: 'text-azure-700', border: 'border-azure-500', light: 'bg-azure-50' }
              };
              const colors = colorClasses[currentTool.color];

              return (
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center text-2xl flex-shrink-0`}>
                    {currentTool.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-xs font-bold text-stone-500 uppercase tracking-wide">Essay Ideas</div>
                      <div className="flex items-center gap-1">
                        {essayIdeationTools.map((_, idx) => (
                          <div
                            key={idx}
                            className={`w-1.5 h-1.5 rounded-full transition-all ${
                              idx === currentToolIndex ? colors.bg : 'bg-stone-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <h3 className="font-bold text-slate-900 mb-1 text-base">{currentTool.title}</h3>
                    <p className={`text-xs font-semibold ${colors.text} mb-2`}>{currentTool.subtitle}</p>
                    <p className="text-sm text-stone-600 mb-3">{currentTool.description}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <Clock className="w-3 h-3" />
                        <span>{currentTool.timeEstimate}</span>
                      </div>
                      <Link
                        to={currentTool.route}
                        className={`text-sm font-medium ${colors.text} hover:opacity-80 flex items-center gap-1 transition-colors`}
                      >
                        Start Interview
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Parent Bridge */}
          <div className="bg-white border border-stone-300 rounded-lg p-4 sm:p-6">
            <h3 className="font-bold text-slate-900 mb-4 text-sm">Parent Bridge</h3>

        

            <ShareButton
              itemType="progress_update"
              itemId={`progress_${currentUser?.id}_${Date.now()}`}
              itemTitle="My College Application Progress"
              currentStatus="current"
              variant="default"
              className="w-full"
              onShareComplete={() => console.log('Progress shared')}
            />

             {/* Note: Primary essay sharing happens in the essay writing interface with specific essay context */}
             <div className="mb-3 text-center py-3 ">
              <p className="text-xs text-stone-600">
                Share specific essays from the essay editor below
              </p>
            </div>
          </div>
        </div>
      </div>
  
   

      {/* Essay Development System */}
      <div className="max-w-7xl mx-auto mb-4 sm:mb-8 mt-4 sm:mt-8 px-4 sm:px-6 lg:px-8">
        <div className="bg-white border-2 border-stone-300 rounded-xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start justify-between mb-4 sm:mb-6 gap-4">
            <div className="flex items-start gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100 flex-shrink-0">
                <Wand2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-slate-900 mb-1 text-sm sm:text-base">Essay Development System</h3>
                <p className="text-xs sm:text-sm text-stone-600 break-words">
                A 4-step process that takes you from empty page to a polished draft (in less time than you'd think).
                </p>
              </div>
            </div>

            {/* Toggle */}
            <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-stone-200 w-full sm:w-auto flex-shrink-0">
              <button
                onClick={() => setEssayFlowType('personal')}
                className={`flex-1 sm:flex-none px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  essayFlowType === 'personal'
                    ? 'bg-indigo-100 text-indigo-900 shadow-sm'
                    : 'text-stone-600 hover:text-slate-900'
                }`}
              >
                Personal Essay
              </button>
              <button
                onClick={() => setEssayFlowType('supplemental')}
                className={`flex-1 sm:flex-none px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  essayFlowType === 'supplemental'
                    ? 'bg-indigo-100 text-indigo-900 shadow-sm'
                    : 'text-stone-600 hover:text-slate-900'
                }`}
              >
                Supplemental
              </button>
            </div>
          </div>

          {/* Personal Essay Flow */}
          {essayFlowType === 'personal' && (
            <>
              <div className="mb-4 text-xs sm:text-sm text-stone-700 bg-stone-50 rounded-lg p-3 border-l-2 border-indigo-500">
                <span className="font-medium">Common App essay:</span> Start from scratch and discover your authentic story
              </div>

              <div className="flex items-stretch gap-0 mb-4 overflow-x-auto pb-2 -mx-2 px-2 sm:mx-0 sm:px-0 snap-x snap-mandatory md:snap-none">
                {/* Step 1: Ideation */}
                <div className="flex-1 min-w-[160px] sm:min-w-[180px] snap-center">
                  <Link to="/college/essays?mode=ideation">
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-500 rounded-lg p-3 sm:p-4 h-full hover:shadow-lg transition-shadow cursor-pointer">
                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-teal-600 text-white font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                        1
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Ideation</div>
                      <div className="text-[11px] sm:text-xs text-stone-600 leading-relaxed">Discover 8-10 strong concepts</div>
                      <div className="mt-2 sm:mt-3 text-[11px] sm:text-xs text-teal-700 font-medium">~20 min</div>
                    </div>
                  </Link>
                </div>

                {/* Connector 1 - Teal to Azure */}
                <div className="flex items-center justify-center px-2 sm:px-3 flex-shrink-0">
                  <div className="h-1 sm:h-1.5 w-8 sm:w-16 rounded-full bg-gradient-to-r from-teal-500 via-cyan-400 to-sky-500"></div>
                </div>

                {/* Step 2: Topic Dev */}
                <div className="flex-1 min-w-[160px] sm:min-w-[180px] snap-center">
                  <Link to="/college/essays?mode=topics&tab=my-ideas">
                    <div className="bg-gradient-to-br from-azure-50 to-azure-100 border-2 border-azure-500 rounded-lg p-3 sm:p-4 h-full hover:shadow-lg transition-shadow cursor-pointer">
                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-azure-600 text-white font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                        2
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Topic Dev</div>
                      <div className="text-[11px] sm:text-xs text-stone-600 leading-relaxed">Build detailed 650-word outline</div>
                      <div className="mt-2 sm:mt-3 text-[11px] sm:text-xs text-azure-700 font-medium">~25 min</div>
                    </div>
                  </Link>
                </div>

                {/* Connector 2 - Azure to Gold */}
                <div className="flex items-center justify-center px-2 sm:px-3 flex-shrink-0">
                  <div className="h-1 sm:h-1.5 w-8 sm:w-16 rounded-full bg-gradient-to-r from-sky-500 via-blue-400 to-amber-500"></div>
                </div>

                {/* Step 3: Builder */}
                <div className="flex-1 min-w-[160px] sm:min-w-[180px] snap-center">
                  <Link to="/college/essays?mode=outlines">
                    <div className="bg-gradient-to-br from-gold-50 to-gold-100 border-2 border-gold-500 rounded-lg p-3 sm:p-4 h-full hover:shadow-lg transition-shadow cursor-pointer">
                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gold-600 text-white font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                        3
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Builder</div>
                      <div className="text-[11px] sm:text-xs text-stone-600 leading-relaxed">Write section-by-section to draft</div>
                      <div className="mt-2 sm:mt-3 text-[11px] sm:text-xs text-gold-700 font-medium">~60 min</div>
                    </div>
                  </Link>
                </div>

                {/* Connector 3 - Gold to Coral */}
                <div className="flex items-center justify-center px-2 sm:px-3 flex-shrink-0">
                  <div className="h-1 sm:h-1.5 w-8 sm:w-16 rounded-full bg-gradient-to-r from-amber-500 via-orange-400 to-rose-500"></div>
                </div>

                {/* Step 4: Analysis */}
                <div className="flex-1 min-w-[160px] sm:min-w-[180px] snap-center">
                  <Link to="/college/essays?mode=workspace">
                    <div className="bg-gradient-to-br from-coral-50 to-coral-100 border-2 border-coral-500 rounded-lg p-3 sm:p-4 h-full hover:shadow-lg transition-shadow cursor-pointer">
                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-coral-600 text-white font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                        4
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Analysis</div>
                      <div className="text-[11px] sm:text-xs text-stone-600 leading-relaxed">Get suggestions to polish</div>
                      <div className="mt-2 sm:mt-3 text-[11px] sm:text-xs text-coral-700 font-medium">~15 min</div>
                    </div>
                  </Link>
                </div>
              </div>

              <div className="p-4 bg-indigo-50 rounded-lg border-l-2 border-indigo-500">
                <div className="text-xs font-bold text-indigo-900 mb-2">Common App essay status</div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-xs">
                    <CheckCircle2 className="w-3 h-3 text-teal-600" />
                    <span className="text-stone-700">Ideation complete</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <CheckCircle2 className="w-3 h-3 text-teal-600" />
                    <span className="text-stone-700">Outline ready</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-full border-2 border-gold-500 bg-gold-100"></div>
                    <span className="text-stone-700 font-medium">Ready for Builder →</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Supplemental Essay Flow */}
          {essayFlowType === 'supplemental' && (
            <>
              {/* Essay Selector */}
              {prioritizedEssays.length > 0 && currentEssay ? (
                <>
            <div className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-8">
              <div className="flex flex-1 flex-col text-xs sm:text-sm text-stone-700 bg-stone-50 rounded-lg p-3 border border-slate-900">
                  {/* College Name, Word Count, and Status */}
                    <div className="flex flex-col gap-2">
                      <div className="font-bold text-slate-900 break-words">
                        {currentEssay.college?.name || currentEssay.title || 'Supplemental Essay'}
                        {currentEssay.application_round && (
                          <span className="ml-2 text-xs font-normal">
                            ({currentEssay.application_round === 'early_decision' ? 'Early Decision' :
                              currentEssay.application_round === 'early_action' ? 'Early Action' : 'Regular Decision'})
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-stone-600">
                        {currentEssay.word_count ? `${currentEssay.word_count} words` :
                         currentEssay.word_limit ? `${currentEssay.word_limit} word limit` :
                         'No word count yet'}
                      </div>
                {/* Essay Status */}
                <div className="flex items-center gap-3 flex-wrap my-2">
                  {essayStatus?.hasOutline && (
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle2 className="w-3 h-3 text-teal-600" />
                      <span className="text-stone-700">Outline complete</span>
                    </div>
                  )}
                  {essayStatus?.draftComplete && (
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle2 className="w-3 h-3 text-teal-600" />
                      <span className="text-stone-700">Draft complete</span>
                    </div>
                  )}
                  {!essayStatus?.hasOutline && !essayStatus?.draftComplete && (
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full border-2 border-azure-500 bg-azure-100"></div>
                      <span className="text-stone-700 font-medium">Ready to begin →</span>
                    </div>
                  )}
                
                </div>
                </div>
                {/* End Name, Word Count, and Status */}
              
                {/* Request Essay Review Button - Available at any stage */}
                {currentEssay && (
                  <div className="w-full sm:w-auto flex-shrink-0">
                    <ShareButton
                      itemType="essay"
                      itemId={currentEssay.id}
                      itemTitle={
                        currentEssay.college?.name
                          ? `${currentEssay.college.name} Essay${currentEssay.prompt ? ': ' + currentEssay.prompt.substring(0, 60) + '...' : ''}`
                          : 'Supplemental Essay'
                      }
                      currentStatus={essayStatus?.draftComplete ? "draft" : "in_progress"}
                      variant="compact"
                      className="w-full justify-center"
                      onShareComplete={() => console.log('Essay shared for review')}
                    />
                  </div>
                )}
            </div>

                    {/* Essay Navigation */}
                    {prioritizedEssays.length > 1 && (
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <button
                          onClick={() => setCurrentEssayIndex(Math.max(0, currentEssayIndex - 1))}
                          disabled={currentEssayIndex === 0}
                          className="p-2 text-stone-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowRight className="w-4 h-4 rotate-180" />
                        </button>
                        <div className="text-xs font-medium text-stone-600">
                          {currentEssayIndex + 1} / {prioritizedEssays.length}
                        </div>
                        <button
                          onClick={() => setCurrentEssayIndex(Math.min(prioritizedEssays.length - 1, currentEssayIndex + 1))}
                          disabled={currentEssayIndex >= prioritizedEssays.length - 1}
                          className="p-2 text-stone-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
           

            </div>
            
                  


              <div className="flex items-stretch gap-0 mb-4 overflow-x-auto pb-2 -mx-2 px-2 sm:mx-0 sm:px-0 snap-x snap-mandatory md:snap-none">
                {/* Step 1: Topic Given */}
                <div className="flex-1 min-w-[170px] sm:min-w-[200px] snap-center">
                  <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-2 border-teal-500 rounded-lg p-3 sm:p-4 h-full">
                    <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-teal-600 text-white font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                      1
                    </div>
                    <div className="text-xs font-bold text-slate-900 mb-2">Topic Given</div>
                    <div className="text-[11px] sm:text-xs text-stone-700 leading-relaxed mb-2 line-clamp-3">
                      {currentEssay.prompt || currentEssay.title || 'Essay prompt not available'}
                    </div>
                  </div>
                </div>

                {/* Connector 1 - Teal to Azure */}
                <div className="flex items-center justify-center px-2 sm:px-3 flex-shrink-0">
                  <div className="h-1 sm:h-1.5 w-8 sm:w-16 rounded-full bg-gradient-to-r from-teal-500 via-cyan-400 to-sky-500"></div>
                </div>

                {/* Step 2: Topic Dev */}
                <div className="flex-1 min-w-[170px] sm:min-w-[200px] snap-center">
                  <Link
                    to={essayStatus?.topicDevClickable ? `/college/essays?mode=topics&expandedCollegeId=${currentEssay.college_id}` : '#'}
                    className={essayStatus?.topicDevClickable ? 'cursor-pointer' : 'pointer-events-none'}
                  >
                    <div className={`bg-gradient-to-br from-azure-50 to-azure-100 border-2 border-azure-500 rounded-lg p-3 sm:p-4 h-full ${essayStatus?.topicDevClickable ? 'hover:shadow-lg transition-shadow' : ''}`}>
                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-azure-600 text-white font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                        2
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Topic Dev</div>
                      <div className="text-[11px] sm:text-xs text-stone-600 leading-relaxed mb-2">Research + build detailed outline</div>
                      {essayStatus?.topicDevStatus && (
                        <div className={`text-[11px] sm:text-xs font-medium ${
                          essayStatus?.hasOutline ? 'text-teal-700 bg-teal-100' :
                          essayStatus?.hasIdea ? 'text-azure-700 bg-azure-100' :
                          essayStatus?.topicDevClickable ? 'text-azure-700 bg-azure-100' :
                          'text-stone-600 bg-stone-100'
                        } rounded px-2 py-1 inline-block`}>
                          {essayStatus.topicDevStatus} {essayStatus?.topicDevClickable && '→'}
                        </div>
                      )}
                    </div>
                  </Link>
                </div>

                {/* Connector 2 - Azure to Gold */}
                <div className="flex items-center justify-center px-2 sm:px-3 flex-shrink-0">
                  <div className="h-1 sm:h-1.5 w-8 sm:w-16 rounded-full bg-gradient-to-r from-sky-500 via-blue-400 to-amber-500"></div>
                </div>

                {/* Step 3: Builder */}
                <div className="flex-1 min-w-[170px] sm:min-w-[200px] snap-center">
                  <Link
                    to={essayStatus?.builderClickable ? `/college/essays?mode=outlines` : '#'}
                    className={essayStatus?.builderClickable ? 'cursor-pointer' : 'pointer-events-none'}
                  >
                    <div className={`bg-gradient-to-br from-gold-50 to-gold-100 border-2 border-gold-500 rounded-lg p-3 sm:p-4 h-full ${essayStatus?.builderClickable ? 'hover:shadow-lg transition-shadow' : ''}`}>
                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gold-600 text-white font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                        3
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Builder</div>
                      <div className="text-[11px] sm:text-xs text-stone-600 leading-relaxed mb-2">Write section-by-section to draft</div>
                      {essayStatus?.builderStatus && (
                        <div className={`text-[11px] sm:text-xs font-medium ${
                          essayStatus?.draftComplete ? 'text-teal-700 bg-teal-100' :
                          essayStatus?.hasDraft ? 'text-gold-700 bg-gold-100' :
                          essayStatus?.builderClickable ? 'text-gold-700 bg-gold-100' :
                          'text-stone-600 bg-stone-100'
                        } rounded px-2 py-1 inline-block`}>
                          {essayStatus.builderStatus} {essayStatus?.builderClickable && '→'}
                        </div>
                      )}
                    </div>
                  </Link>
                </div>

                {/* Connector 3 - Gold to Coral */}
                <div className="flex items-center justify-center px-2 sm:px-3 flex-shrink-0">
                  <div className="h-1 sm:h-1.5 w-8 sm:w-16 rounded-full bg-gradient-to-r from-amber-500 via-orange-400 to-rose-500"></div>
                </div>

                {/* Step 4: Analysis */}
                <div className="flex-1 min-w-[170px] sm:min-w-[200px] snap-center">
                  <Link
                    to={essayStatus?.draftComplete && currentEssay ? `/college/essays?mode=workspace&slot=${currentEssay.id}` : '/college/essays?mode=workspace'}
                    className={essayStatus?.draftComplete ? 'cursor-pointer' : 'pointer-events-none opacity-60'}
                  >
                    <div className={`bg-gradient-to-br from-coral-50 to-coral-100 border-2 border-coral-500 rounded-lg p-3 sm:p-4 h-full ${essayStatus?.draftComplete ? 'hover:shadow-lg transition-shadow' : ''}`}>
                      <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-coral-600 text-white font-bold mb-2 sm:mb-3 text-sm sm:text-base">
                        4
                      </div>
                      <div className="text-xs font-bold text-slate-900 mb-1">Analysis</div>
                      <div className="text-[11px] sm:text-xs text-stone-600 leading-relaxed">Get suggestions to polish</div>
                      <div className="mt-2 sm:mt-3 text-[11px] sm:text-xs text-coral-700 font-medium">~10 min</div>
                    </div>
                  </Link>
                </div>
              </div>

              
                </>
              ) : (
                <div className="mb-4 text-sm text-stone-700 bg-stone-50 rounded-lg p-4 border-l-2 border-stone-300 text-center">
                  <p className="text-stone-600">No supplemental essays yet. Add colleges to your list to see their essay requirements.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
